import { Button, styled, Box, Typography, List, ListItem, ListItemText, Alert, Divider } from '@mui/material';
import { FC, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { calendarEntriesShiftTemplateApi } from 'api';
import AppModal from 'components/AppModal';
import { EventImpl } from '@fullcalendar/core/internal';
import { H2 } from 'components/Typography';
import { useSeason } from 'contexts/SeasonContext';
import DeleteForeverIcon from '@mui/icons-material/DeleteForever';
import FlexBox from 'components/flexbox/FlexBox';

interface ModalProps {
    open: boolean;
    onClose: () => void;
    fetchCalendarData: () => void;
    event?: EventImpl;
    departmentId?: number;
}

interface AffectedAssignment {
    id: number;
    employeeId: number;
    date: string;
    shiftDetailId: number;
    deleted: boolean;
    published: boolean;
    employee: {
        id: number;
        firstName: string;
        lastName: string;
        email: string | null;
    };
    shiftDetail: {
        id: number;
        shift: {
            id: number;
            shiftTemplate: {
                id: number;
                name: string;
            };
        };
        workPosition: {
            id: number;
            name: string;
        };
    };
}

interface DryRunResponse {
    calendarEntryShiftTemplateId: number;
    affectedAssignmentsCount: number;
    affectedAssignments: AffectedAssignment[];
}

const StyledAppModal = styled(AppModal)(({ theme }) => ({
    maxWidth: 450,
    minWidth: 200,

    [theme.breakpoints.down(325)]: { maxWidth: '100%' },
}));

const StyledConfirmationModal = styled(AppModal)(({ theme }) => ({
    maxWidth: 600,
    minWidth: 300,

    [theme.breakpoints.down(325)]: { maxWidth: '100%' },
}));


const ViewEventModalShift: FC<ModalProps> = ({ open, onClose, fetchCalendarData, event, departmentId }) => {
    const { t } = useTranslation();
    const { seasonId } = useSeason();
    const [showConfirmation, setShowConfirmation] = useState(false);
    const [affectedAssignments, setAffectedAssignments] = useState<AffectedAssignment[]>([]);
    const [isDeleting, setIsDeleting] = useState(false);

    async function handleDeleteClick() {
        if (!event?.id) return;

        try {
            setIsDeleting(true);
            // Call dry-run endpoint to check for affected assignments
            const response = await calendarEntriesShiftTemplateApi.dryRunDelete(Number(event.id)) as any;
            const dryRunData: DryRunResponse = response.data || response;

            if (dryRunData.affectedAssignmentsCount > 0) {
                // Show confirmation modal with affected assignments
                setAffectedAssignments(dryRunData.affectedAssignments);
                setShowConfirmation(true);
            } else {
                // No assignments affected, proceed with deletion immediately
                await performDelete();
            }
        } catch (error: any) {
            console.error('Error checking dry-run delete:', error);
            // If dry-run fails, still allow deletion (fallback behavior)
            await performDelete();
        } finally {
            setIsDeleting(false);
        }
    }

    async function performDelete() {
        if (!event?.id) return;

        try {
            await calendarEntriesShiftTemplateApi.remove(Number(event.id), seasonId, {
                // departmentId is added for depchief security check in backend
                params: {
                    departmentId
                }
            });
            fetchCalendarData();
            onClose();
            setShowConfirmation(false);
            setAffectedAssignments([]);
        } catch (error: any) {
            console.error('Error deleting calendar entry:', error);
            throw error;
        }
    }

    async function handleConfirmDelete() {
        await performDelete();
    }

    function handleCloseConfirmation() {
        setShowConfirmation(false);
        setAffectedAssignments([]);
    }

    const formatDate = (dateString: string) => {
        return new Date(dateString).toLocaleDateString('it-IT');
    };

    return (
        <>
            <StyledAppModal open={open} handleClose={onClose}>
                <H2 mb={2}>
                    {event?.title}
                </H2>
                <Button
                    size='small'
                    color='error'
                    variant='contained'
                    onClick={handleDeleteClick}
                    disabled={isDeleting}
                    sx={{ color: 'common.white' }}
                >
                    {isDeleting ? t('common.loading') || 'Loading...' : t('employees.calendar.entry.deleteEntry')}
                </Button>
            </StyledAppModal>

            <StyledConfirmationModal open={showConfirmation} handleClose={handleCloseConfirmation}>
                <H2 mb={2}>
                    {t('shift.confirmDeleteTemplate') || 'Confirm Deletion'}
                </H2>
                
                <Alert severity="warning" sx={{ mb: 2 }}>
                    <Typography variant="body2">
                        {t('shift.deleteTemplateWarning', { 
                            count: affectedAssignments.length 
                        }) || `Deleting this shift template will also delete ${affectedAssignments.length} employee assignment(s).`}
                    </Typography>
                </Alert>

                {affectedAssignments.length > 0 && (
                    <Box sx={{ mb: 2 }}>
                        <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 'bold' }}>
                            {t('shift.affectedAssignments') || 'Affected Employee Assignments:'}
                        </Typography>
                        <Box 
                            sx={{ 
                                maxHeight: '300px', 
                                overflowY: 'auto',
                                border: '1px solid',
                                borderColor: 'divider',
                                borderRadius: 1,
                                p: 1
                            }}
                        >
                            <List dense>
                                {affectedAssignments.map((assignment, index) => (
                                    <Box key={assignment.id}>
                                        <ListItem>
                                            <ListItemText
                                                primary={
                                                    <Typography variant="body2">
                                                        <strong>{assignment.employee.firstName} {assignment.employee.lastName}</strong>
                                                        {assignment.employee.email && ` (${assignment.employee.email})`}
                                                    </Typography>
                                                }
                                                secondary={
                                                    <Typography variant="caption" color="text.secondary">
                                                        {assignment.shiftDetail.shift.shiftTemplate.name} - {assignment.shiftDetail.workPosition.name}
                                                        <br />
                                                        {formatDate(assignment.date)}
                                                        {assignment.published && (
                                                            <span style={{ color: 'orange', marginLeft: '8px' }}>
                                                                ({t('shift.published') || 'Published'})
                                                            </span>
                                                        )}
                                                    </Typography>
                                                }
                                            />
                                        </ListItem>
                                        {index < affectedAssignments.length - 1 && <Divider />}
                                    </Box>
                                ))}
                            </List>
                        </Box>
                    </Box>
                )}

                <FlexBox justifyContent="flex-end" gap={2} mt={3}>
                    <Button
                        variant="contained"
                        color="primary"
                        onClick={handleCloseConfirmation}
                    >
                        {t('dialog.cancel') || 'Cancel'}
                    </Button>
                    <Button
                        variant="contained"
                        color="error"
                        onClick={handleConfirmDelete}
                        startIcon={<DeleteForeverIcon />}
                        sx={{ color: 'common.white' }}
                    >
                        {t('dialog.confirm') || 'Confirm Delete'}
                    </Button>
                </FlexBox>
            </StyledConfirmationModal>
        </>
    )
};

export default ViewEventModalShift;
